<template>
    <div class="border border-border bg-white rounded-lgr p-2.5 max-h-56 h-fit flex flex-col gap-2">
        <p class="whitespace-nowrap scaffold-body-small-medium">Inferred from</p>
        <HorizontalDivider class="my-1 border-border" />
        <div class="grid max-md:grid-cols-2 gap-2">
            <div v-if="isFetchingLinkedSources">
                <SkeletonLoader class="w-full h-4 mb-2 rounded-lgr" />
                <SkeletonLoader class="w-full h-4 rounded-lgr" />
            </div>
            <template v-if="files.length > 0 || perspectiveTitle">
                <div v-for="file in files.slice(0, 2)" :key="file.name" class="flex gap-2 items-center">
                    <i class="bi bi-file-earmark-text text-valence-primary-500" aria-hidden="true"></i>
                    <p class="inferred-item">{{ truncate(file.name, { length: 24, omission: "..." }) }}</p>
                </div>
                <div v-if="perspectiveTitle" class="flex gap-2 items-center">
                    <ArchetypeDot :letters="perspectiveLetters" class="size-3" />
                    <p class="inferred-item">{{ perspectiveTitle }}</p>
                </div>
                <p v-if="files.length > 2" class="inferred-item">and {{ files.length - 2 }} more...</p>
            </template>
            <p v-else class="inferred-item">
                No sources are available, <br />
                click edit sources to add
            </p>
        </div>
        <BaseButton theme="neutral" size="sm" class="mt-auto flex gap-1 self-end md:self-start items-center" @click="emit('add-source')">
            <i class="bi bi-pen text-sm/none" aria-hidden="true"></i> Edit Sources
        </BaseButton>
    </div>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import SkeletonLoader from "~vue/components/SkeletonLoader.vue";
import { useProfileSources } from "~vue/composables/useProfileSources";
import ArchetypeDot from "~vue/Perspective/ArchetypeDot.vue";
import { getPerspectiveProfileTitle } from "~vue/utils/perspectiveUtils";
import truncate from "lodash/truncate";
import { computed } from "vue";

type InferredDocumentsProps = {
    perspectiveLetters?: string | null;
    topicName?: string | null;
};

const { perspectiveLetters, topicName } = defineProps<InferredDocumentsProps>();

const perspectiveTitle = computed(() => getPerspectiveProfileTitle(perspectiveLetters));

const emit = defineEmits<{
    "add-source": [];
}>();

const { isFetchingLinkedSources, linkedSources } = useProfileSources();

const files = computed(() => linkedSources.value?.sources.filter((s) => s.topic === topicName && s.last_used_at != null) ?? []);
</script>

<style scoped>
@reference "../../../css/tailwind.css";

.inferred-item {
    @apply scaffold-body-small whitespace-nowrap;
}
</style>
